import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import type {
	MediaRenditionsModel,
	MediaSizeModel,
	MediaStatus,
	MediaType,
} from '../../media/media-types';
import { BaseEntity } from './base.entity';

/**
 * PD-16339 — one item in the tenant's media library: a picture, an SVG logo
 * or a PDF. Design: docs/claude/website-editor/08-site-builder-analysis.md §4.5.
 *
 * The ORIGINAL is an ordinary private `_file` (fk__file__id) uploaded through
 * the standard files pipeline; only the generated public files (WebP sizes,
 * the sanitized SVG, the PDF) are on the CDN, at addresses built from
 * `publicId` (see mediaObjectKey). Content elsewhere stores `publicId` only.
 *
 * Brand scope: `fk_brand__id` null means Shared (every brand of the tenant).
 * Who can see or change an item follows company access through the brand
 * (MediaAccessService).
 */
@Index('uq_media_asset_public_id', ['publicId'], { unique: true })
@Index('idx_media_asset_brand', ['fkBrandId'])
@Index('idx_media_asset_folder', ['fkMediaFolderId'])
@Index('idx_media_asset_status', ['status'])
@Entity('media_asset')
export class MediaAssetEntity extends BaseEntity {
	// Random UUID; the only id that ever appears in an address or in content.
	@Column('varchar', { name: 'publicId', length: 36 })
	public publicId!: string;

	// The current original (private). Earlier originals live in media_asset_version.
	@Column('int', { name: 'fk__file__id', unsigned: true, nullable: true })
	public fkFileId!: number | null;

	// Null = Shared across every brand of the tenant.
	@Column('int', { name: 'fk_brand__id', unsigned: true, nullable: true })
	public fkBrandId!: number | null;

	@Column('int', {
		name: 'fk_media_folder__id',
		unsigned: true,
		nullable: true,
	})
	public fkMediaFolderId!: number | null;

	@Column('varchar', { name: 'mediaType', length: 16 })
	public mediaType!: MediaType;

	@Column('varchar', { name: 'status', length: 16, default: 'uploading' })
	public status!: MediaStatus;

	// Written for the person who uploaded the file, shown in the library.
	@Column({
		type: 'varchar',
		name: 'failureReason',
		length: 500,
		nullable: true,
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public failureReason!: string | null;

	// Defaults to the file name without its extension.
	@Column({
		type: 'varchar',
		name: 'title',
		length: 255,
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public title!: string;

	@Column({
		type: 'varchar',
		name: 'originalName',
		length: 255,
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public originalName!: string;

	// As measured from the bytes by the processing job, not as the browser claimed.
	@Column('varchar', { name: 'contentType', length: 100, nullable: true })
	public contentType!: string | null;

	// Default alternative text; a use can override it.
	@Column({
		type: 'varchar',
		name: 'altText',
		length: 500,
		nullable: true,
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public altText!: string | null;

	@Column({
		type: 'varchar',
		name: 'caption',
		length: 1000,
		nullable: true,
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public caption!: string | null;

	@Column({
		type: 'text',
		name: 'description',
		nullable: true,
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public description!: string | null;

	// Photographer or license note, for stock pictures.
	@Column({
		type: 'varchar',
		name: 'credit',
		length: 255,
		nullable: true,
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public credit!: string | null;

	// Of the original.
	@Column('int', { name: 'width', unsigned: true, nullable: true })
	public width!: number | null;

	@Column('int', { name: 'height', unsigned: true, nullable: true })
	public height!: number | null;

	@Column('int', { name: 'bytes', unsigned: true, nullable: true })
	public bytes!: number | null;

	// 0 to 1 from the left and top: where crops center (hero banners, square tiles).
	@Column('double', { name: 'focalX', nullable: true })
	public focalX!: number | null;

	@Column('double', { name: 'focalY', nullable: true })
	public focalY!: number | null;

	// The generated public sizes (pictures only).
	@Column('json', { name: 'sizes', nullable: true })
	public sizes!: Array<MediaSizeModel> | null;

	// Other public files, e.g. the email-safe copy (email clients cannot show WebP or SVG).
	@Column('json', { name: 'renditions', nullable: true })
	public renditions!: MediaRenditionsModel | null;

	// A replacement (or restored earlier original) being processed. The item keeps
	// serving its current files until this succeeds, so a bad file never breaks a use.
	@Column('int', {
		name: 'fk__file__id_pending',
		unsigned: true,
		nullable: true,
	})
	public fkFileIdPending!: number | null;

	// Set when a deleted item's files were removed after the restore window.
	@Column('datetime', { name: 'purgedDate', nullable: true })
	public purgedDate!: Date | null;

	// Bumped on every replace; earlier originals are in media_asset_version.
	@Column('int', { name: 'version', unsigned: true, default: () => "'1'" })
	public version!: number;
}
